import { useEffect, useRef, useState } from 'react';
import { CreditCard, HelpCircle, LogOut, MoreVertical, Settings, SunMoon } from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { SettingsModal } from './SettingsModal';
import { HelpModal } from './HelpModal';
import { PlanModal } from './PlanModal';
import { useT } from '../I18nContext';

/**
 * User-card menu in the sidebar's bottom rail.
 *
 * Items:
 *   Settings → SettingsModal (appearance, future settings)
 *   Help     → HelpModal (static reference)
 *   Theme    → flip dark/light (Match-system lives in Settings)
 *   Log out  → server-side /logout redirect
 *
 * Click-outside + Escape both close the dropdown so it never feels
 * sticky. role="menu" for assistive tech.
 */
export function UserMenu() {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const { toggle, theme } = useTheme();
  const t = useT();

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div className="user-menu-wrap" ref={wrapRef}>
        <button
          type="button"
          className="user-menu-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t('user_menu.trigger_aria_label')}
        >
          <MoreVertical size={16} />
        </button>

        {open && (
          <div className="user-menu-dropdown" role="menu">
            <button
              type="button"
              className="user-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setPlanOpen(true);
              }}
            >
              <CreditCard size={14} />
              <span>{t('user_menu.plan')}</span>
            </button>
            <button
              type="button"
              className="user-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setSettingsOpen(true);
              }}
            >
              <Settings size={14} />
              <span>{t('user_menu.settings')}</span>
            </button>
            <button
              type="button"
              className="user-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setHelpOpen(true);
              }}
            >
              <HelpCircle size={14} />
              <span>{t('user_menu.help')}</span>
            </button>
            <button
              type="button"
              className="user-menu-item"
              role="menuitem"
              onClick={() => {
                toggle();
                // Leave the menu open so the user sees the icon flip
                // confirm - they often want to compare themes quickly.
              }}
            >
              <SunMoon size={14} />
              <span>{theme === 'dark' ? t('user_menu.theme_light') : t('user_menu.theme_dark')}</span>
            </button>
            <div className="user-menu-divider" />
            <a
              className="user-menu-item user-menu-item-danger"
              role="menuitem"
              // Server-side logout - clears Flask-Login session and
              // redirects to /login. JSON logout is a future PR.
              href="/logout"
            >
              <LogOut size={14} />
              <span>{t('user_menu.log_out')}</span>
            </a>
          </div>
        )}
      </div>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
      {planOpen && <PlanModal onClose={() => setPlanOpen(false)} />}
    </>
  );
}
